'use client';

import { c as _c } from "react/compiler-runtime";
import { usePublicUserIdentity } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { type GetInitialsMaxChars, getInitials, getPaletteEntry } from '@/components/images/user-avatar-utils';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useNsfwStatusByUrl } from '@/hooks/use-nsfw-status-by-url';
import { sendLog } from '@/lib/telemetry/client';
import { cn } from '@/lib/ui/utils';
import { useUploadTrackingStore } from '@/stores/upload-tracking';
export type UserAvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';

/** Thickness token for the optional avatar frame (`ring-{n}` in Tailwind). */
export type UserAvatarBorderWidth = 1 | 2 | 4;
export interface UserAvatarProps {
  /**
   * Immutable user identifier. Used by the pattern-studio façade as both
   * the brand-blue palette seed and the manifest seed, so the same user
   * looks identical everywhere on the platform regardless of display
   * name changes. For wallet-shaped data the wallet `address` (not `id`)
   * is acceptable — it is stable per user.
   *
   * When `null`/`undefined`, the wrapper falls back to a `displayName`-
   * seeded fallback tile and emits a dev-only `sendLog` warning so
   * missing plumbing surfaces in test runs.
   */
  userId: string | null | undefined;
  displayName: string | null;
  avatarUrl: string | null;
  size?: UserAvatarSize;
  className?: string;
  /** Maximum initials shown when the user has no uploaded avatar photo. */
  initialsMaxChars?: GetInitialsMaxChars;
  /**
   * Optional frame around the avatar. Implemented with Tailwind `ring-*`
   * (not `border`) so the photo/pattern fills the full circular clip — a real
   * border shrinks the content box and breaks `absolute inset-0` sizing from
   * `<OptimizedImage variant="avatar" />`.
   */
  borderWidth?: UserAvatarBorderWidth;
  /**
   * Tailwind colour utility for the frame. Pass `border-*` tokens (e.g.
   * `border-border-primary`) or `ring-*` — `border-` is mapped to `ring-`
   * automatically. When `borderWidth` is set and this is omitted, defaults to
   * `border-border-primary`.
   */
  borderColor?: string;
  /**
   * Optional stable string to seed the deterministic palette when both
   * `userId` and `displayName` are null/empty. Lets anonymous traders
   * render with visually distinct avatar colours (palette is hashed from
   * the seed) while the initials remain the standard "AN" glyph.
   *
   * No effect when `displayName` is set — palette is always derived from
   * the displayName so the same user looks identical everywhere.
   */
  colorSeed?: string;
  /**
   * When `true`, clicking the avatar opens the global lightbox preview.
   * Default `false` because UserAvatar is the canonical avatar wrapper
   * used in many already-clickable contexts (wallet dropdown, leaderboard
   * rows, profile links) where nesting a button would be invalid HTML.
   * Opt in for surfaces like a non-owner portfolio header where there is
   * no other click handler.
   */
  preview?: boolean;
  /**
   * When `true`, the underlying photo is rendered through the
   * NSFW-blur overlay until the viewer taps to reveal. Comes from the
   * SDK `avatarNsfw` field on user/profile responses (spec 071). Default
   * `false` — pass through `user.avatarNsfw ?? false` at every callsite
   * so flagged avatars are blurred consistently across the app.
   */
  nsfw?: boolean;
  /**
   * Per-user preference: `true` renders the avatar through the
   * pattern-studio rasteriser (dithered, brand-graded), `false` shows the
   * raw upload directly when present. Sourced from the SDK
   * `avatarPreferences.rasterise` field on user/profile responses.
   * Required prop — every callsite must pass it explicitly (mirrors the
   * `nsfw` discipline) so silent defaults can't drift the global look.
   * Use `user.avatarPreferences?.rasterise ?? false` as the standard
   * extraction at callsites — the `?? false` covers transitional periods
   * where an SDK response lacks the field (rasterised look is opt-in).
   */
  rasterise: boolean;
}
const SIZE_CLASSES: Record<UserAvatarSize, string> = {
  xs: 'size-6',
  sm: 'size-8',
  md: 'size-10',
  lg: 'size-12',
  xl: 'size-14',
  '2xl': 'size-20',
  '3xl': 'size-24'
} as const;
const PIXEL_SIZES: Record<UserAvatarSize, number> = {
  xs: 24,
  sm: 32,
  md: 40,
  lg: 48,
  xl: 56,
  '2xl': 80,
  '3xl': 96
} as const;
const INITIALS_TEXT_CLASSES: Record<UserAvatarSize, string> = {
  xs: 'text-caption font-medium',
  sm: 'text-caption font-medium',
  md: 'text-body font-medium',
  lg: 'text-body font-medium',
  xl: 'text-body-lg font-medium',
  '2xl': 'text-heading-md font-bold',
  '3xl': 'text-heading font-bold'
} as const;
const NEUTRAL_FALLBACK_CLASSES = 'bg-background-tertiary text-foreground-secondary';

/** Ring width — drawn outside the avatar box so image fills the circle. */
const FRAME_WIDTH_CLASSES: Record<UserAvatarBorderWidth, string> = {
  1: 'ring-1',
  2: 'ring-2',
  4: 'ring-4'
} as const;
const DEFAULT_FRAME_COLOR_BORDER_SYNTAX = 'border-border-primary';

/** Map consumer `border-*` colour tokens to `ring-*` for the frame. */
function frameColorClass(colorClass: string): string {
  const t = colorClass.trim();
  if (t.startsWith('ring-')) {
    return t;
  }
  if (t.startsWith('border-')) {
    return `ring-${t.slice('border-'.length)}`;
  }
  return t;
}
function isAnonymous(displayName: string | null): boolean {
  return displayName === null || displayName.length === 0;
}
interface UserAvatarImageProps {
  userId: string;
  displayName: string | null;
  avatarUrl: string | null;
  trackedAvatarUrl: string | null;
  rasterise: boolean;
  nsfw: boolean;
  altText: string;
  size: UserAvatarSize;
  preview: boolean;
  /** Fired when every image URL in the chain failed to load (MOR-1843). */
  onExhausted: () => void;
}
interface TrackedUserAvatarImageProps extends Omit<UserAvatarImageProps, 'avatarUrl'> {
  avatarUrl: string;
}
function UserAvatarImage(props) {
  const $ = _c(7);
  const trackedAvatarUrl = props.trackedAvatarUrl;
  let t0;
  if ($[0] !== trackedAvatarUrl) {
    t0 = state => trackedAvatarUrl === null ? false : state.urlToSha.has(trackedAvatarUrl);
    $[0] = trackedAvatarUrl;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const uploadTracked = useUploadTrackingStore(t0);
  if (uploadTracked && trackedAvatarUrl !== null) {
    let t1;
    if ($[2] !== props || $[3] !== trackedAvatarUrl) {
      t1 = <TrackedUserAvatarImage {...props} avatarUrl={trackedAvatarUrl} />;
      $[2] = props;
      $[3] = trackedAvatarUrl;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  let t1;
  if ($[5] !== props) {
    t1 = <UserAvatarImageContent {...props} />;
    $[5] = props;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
}
function TrackedUserAvatarImage(props) {
  const $ = _c(19);
  const liveModeration = useNsfwStatusByUrl(props.avatarUrl);
  const liveModerationSettled = liveModeration.status !== undefined && liveModeration.status !== "pending";
  let t0;
  if ($[0] !== props.rasterise) {
    t0 = {
      rasterise: props.rasterise
    };
    $[0] = props.rasterise;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== liveModeration.isFlagged || $[3] !== props.avatarUrl || $[4] !== props.displayName || $[5] !== props.userId || $[6] !== t0) {
    t1 = {
      userId: props.userId,
      displayName: props.displayName,
      avatarUrl: props.avatarUrl,
      avatarNsfw: liveModeration.isFlagged,
      avatarPreferences: t0
    };
    $[2] = liveModeration.isFlagged;
    $[3] = props.avatarUrl;
    $[4] = props.displayName;
    $[5] = props.userId;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== liveModeration.isFlagged || $[10] !== liveModerationSettled || $[11] !== props.avatarUrl || $[12] !== props.userId) {
    t3 = liveModerationSettled ? {
      avatarModeration: {
        userId: props.userId,
        avatarUrl: props.avatarUrl,
        avatarNsfw: liveModeration.isFlagged
      }
    } : undefined;
    $[9] = liveModeration.isFlagged;
    $[10] = liveModerationSettled;
    $[11] = props.avatarUrl;
    $[12] = props.userId;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const moderatedIdentity = usePublicUserIdentity(t1, t2, t3);
  let t4;
  if ($[14] !== liveModeration.isFlagged || $[15] !== moderatedIdentity.avatarPreferences.rasterise || $[16] !== moderatedIdentity.displayName || $[17] !== props) {
    t4 = <UserAvatarImageContent {...props} displayName={moderatedIdentity.displayName} rasterise={moderatedIdentity.avatarPreferences.rasterise} nsfw={liveModeration.isFlagged} />;
    $[14] = liveModeration.isFlagged;
    $[15] = moderatedIdentity.avatarPreferences.rasterise;
    $[16] = moderatedIdentity.displayName;
    $[17] = props;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  return t4;
}
function UserAvatarImageContent(t0) {
  const $ = _c(12);
  const {
    userId,
    displayName,
    avatarUrl,
    rasterise,
    nsfw,
    altText,
    size,
    preview,
    onExhausted
  } = t0;
  let t1;
  if ($[0] !== avatarUrl || $[1] !== displayName || $[2] !== nsfw || $[3] !== rasterise || $[4] !== userId) {
    t1 = {
      kind: "user",
      userId,
      displayName,
      avatarUrl,
      rasterise,
      nsfw
    };
    $[0] = avatarUrl;
    $[1] = displayName;
    $[2] = nsfw;
    $[3] = rasterise;
    $[4] = userId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const t2 = PIXEL_SIZES[size];
  let t3;
  if ($[6] !== altText || $[7] !== onExhausted || $[8] !== preview || $[9] !== t1 || $[10] !== t2) {
    t3 = <OptimizedImage variant="avatar" entity={t1} alt={altText} size={t2} className="absolute inset-0 object-cover" preview={preview} onExhausted={onExhausted} />;
    $[6] = altText;
    $[7] = onExhausted;
    $[8] = preview;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}
export function UserAvatar(t0) {
  const $ = _c(49);
  const {
    userId,
    displayName,
    avatarUrl,
    size: t1,
    className,
    initialsMaxChars,
    borderWidth,
    borderColor,
    colorSeed,
    preview: t2,
    nsfw: t3,
    rasterise
  } = t0;
  const size = t1 === undefined ? "md" : t1;
  const preview = t2 === undefined ? false : t2;
  const nsfw = t3 === undefined ? false : t3;
  const t = useTranslations();
  let t4;
  if ($[0] !== rasterise) {
    t4 = {
      rasterise
    };
    $[0] = rasterise;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== avatarUrl || $[3] !== displayName || $[4] !== nsfw || $[5] !== t4 || $[6] !== userId) {
    t5 = {
      userId,
      displayName,
      avatarUrl,
      avatarNsfw: nsfw,
      avatarPreferences: t4
    };
    $[2] = avatarUrl;
    $[3] = displayName;
    $[4] = nsfw;
    $[5] = t4;
    $[6] = userId;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const identity = usePublicUserIdentity(t5);
  const canonicalDisplayName = identity.displayName;
  const canonicalAvatarUrl = identity.avatarUrl;
  const canonicalNsfw = identity.avatarNsfw ?? false;
  const canonicalRasterise = identity.avatarPreferences?.rasterise ?? false;
  const t6 = initialsMaxChars ?? (size === "xs" ? 1 : 2);
  let t7;
  if ($[8] !== canonicalDisplayName || $[9] !== t6) {
    t7 = getInitials(canonicalDisplayName, {
      maxChars: t6
    });
    $[8] = canonicalDisplayName;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const initials = t7;
  let t8;
  if ($[11] !== canonicalDisplayName || $[12] !== t) {
    t8 = isAnonymous(canonicalDisplayName) ? t("profile.anonymous") : canonicalDisplayName as string;
    $[11] = canonicalDisplayName;
    $[12] = t;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const altText = t8;
  const hasUserId = userId !== null && userId !== undefined && userId.length > 0;
  if (!hasUserId && process.env.NODE_ENV !== "production") {
    sendLog("UserAvatar: userId missing", "warn", {
      context: "UserAvatar"
    });
  }
  const [photoExhausted, setPhotoExhausted] = useState(false);
  const hasUploadedPhoto = typeof canonicalAvatarUrl === "string" && canonicalAvatarUrl.length > 0 && !photoExhausted;
  const hasColorSeed = colorSeed !== undefined && colorSeed.length > 0;
  let t9;
  if ($[14] !== canonicalDisplayName || $[15] !== colorSeed || $[16] !== hasColorSeed) {
    t9 = isAnonymous(canonicalDisplayName) ? hasColorSeed ? (() => {
      const entry = getPaletteEntry(colorSeed as string);
      return `${entry.bg} ${entry.text}`;
    })() : NEUTRAL_FALLBACK_CLASSES : (() => {
      const entry_0 = getPaletteEntry(canonicalDisplayName as string);
      return `${entry_0.bg} ${entry_0.text}`;
    })();
    $[14] = canonicalDisplayName;
    $[15] = colorSeed;
    $[16] = hasColorSeed;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const fallbackTintClasses = t9;
  const showFrame = borderWidth !== undefined;
  const frameWidthClass = showFrame ? FRAME_WIDTH_CLASSES[borderWidth] : undefined;
  let T0;
  let t10;
  if ($[18] !== borderColor || $[19] !== className || $[20] !== frameWidthClass || $[21] !== showFrame || $[22] !== size) {
    const frameColorClassName = showFrame ? frameColorClass(borderColor !== undefined && borderColor.length > 0 ? borderColor : DEFAULT_FRAME_COLOR_BORDER_SYNTAX) : undefined;
    T0 = Avatar;
    t10 = cn(SIZE_CLASSES[size], "bg-transparent", showFrame && "ring-offset-0", frameWidthClass, frameColorClassName, className);
    $[18] = borderColor;
    $[19] = className;
    $[20] = frameWidthClass;
    $[21] = showFrame;
    $[22] = size;
    $[23] = T0;
    $[24] = t10;
  } else {
    T0 = $[23];
    t10 = $[24];
  }
  let t11;
  if ($[25] !== altText || $[26] !== avatarUrl || $[27] !== canonicalAvatarUrl || $[28] !== canonicalDisplayName || $[29] !== canonicalNsfw || $[30] !== canonicalRasterise || $[31] !== hasUploadedPhoto || $[32] !== hasUserId || $[33] !== preview || $[34] !== size || $[35] !== userId) {
    t11 = hasUserId ? <UserAvatarImage userId={userId as string} displayName={canonicalDisplayName} avatarUrl={canonicalAvatarUrl} trackedAvatarUrl={avatarUrl} rasterise={canonicalRasterise} nsfw={hasUploadedPhoto && canonicalNsfw} altText={altText} size={size} preview={preview} onExhausted={() => setPhotoExhausted(true)} /> : null;
    $[25] = altText;
    $[26] = avatarUrl;
    $[27] = canonicalAvatarUrl;
    $[28] = canonicalDisplayName;
    $[29] = canonicalNsfw;
    $[30] = canonicalRasterise;
    $[31] = hasUploadedPhoto;
    $[32] = hasUserId;
    $[33] = preview;
    $[34] = size;
    $[35] = userId;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== fallbackTintClasses || $[38] !== hasUploadedPhoto || $[39] !== hasUserId || $[40] !== initials || $[41] !== photoExhausted || $[42] !== size) {
    t12 = hasUploadedPhoto ? null : <AvatarFallback className={cn(INITIALS_TEXT_CLASSES[size], hasUserId && !photoExhausted ? "bg-transparent text-white drop-shadow-md" : photoExhausted ? cn("relative", fallbackTintClasses) : fallbackTintClasses)}>{initials}</AvatarFallback>;
    $[37] = fallbackTintClasses;
    $[38] = hasUploadedPhoto;
    $[39] = hasUserId;
    $[40] = initials;
    $[41] = photoExhausted;
    $[42] = size;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  let t13;
  if ($[44] !== T0 || $[45] !== t10 || $[46] !== t11 || $[47] !== t12) {
    t13 = <T0 className={t10}>{t11}{t12}</T0>;
    $[44] = T0;
    $[45] = t10;
    $[46] = t11;
    $[47] = t12;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  return t13;
}